import React, { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { hämtaMoten, hämtaKundförslag, hämtaLagervarningar, hämtaOklaraMatchningar, hämtaDubbletter, hämtaIntagsfel } from '../api/client';
import { TEMA } from '../theme';
import { useDemo } from '../demo';
import { hämtaAntalAttÅtgärda } from '../api/inkop';
import Modulvaljare, { useAktivModul, useTillgängligaModuler } from './Modulvaljare';

interface SidebarProps {
  // Anropas efter navigering, så mobilmenyn kan stänga sig själv.
  // Lämnas tom på desktop.
  onNavigate?: () => void;
}

// Sidorna under "Att åtgärda" — listor med saker som väntar på handling.
const ÅTGÄRDA_SIDOR = ['/att-koppla', '/inlasningsfel', '/nya-fortnox-kunder', '/lager', '/artikelmatchning', '/dubbletter'];

export default function Sidebar({ onNavigate }: SidebarProps) {
  const { profil, loggaUt } = useAuth();
  // I demon visas inte sidorna som ännu bara är platshållare.
  const demo = useDemo();
  const navigate = useNavigate();
  const location = useLocation();
  const [sökning, setSökning] = useState('');
  const [attKopplaAntal, setAttKopplaAntal] = useState(0);
  const [fortnoxAntal, setFortnoxAntal] = useState(0);
  const [lagerAntal, setLagerAntal] = useState(0);
  const [matchAntal, setMatchAntal] = useState(0);
  const [dubblettAntal, setDubblettAntal] = useState(0);
  const [intagsfelAntal, setIntagsfelAntal] = useState(0);
  const [inkopAntal, setInkopAntal] = useState(0);
  const harInkop = useTillgängligaModuler().some(m => m.id === 'inkop');
  // Dubblettsidan meddelar när listan ändrats, så att räknaren inte väntar
  // till nästa sidbyte.
  useEffect(() => {
    const uppdatera = () => {
      hämtaDubbletter().then(res => setDubblettAntal(res.data.length)).catch(() => {});
    };
    window.addEventListener('vinsales:dubbletter-andrade', uppdatera);
    return () => window.removeEventListener('vinsales:dubbletter-andrade', uppdatera);
  }, []);

  // Något har sparats: hämta räknarna på nytt. Fördröjt, så att flera snabba
  // sparanden i rad bara ger en hämtning.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const uppdatera = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        hämtaOklaraMatchningar().then(res => setMatchAntal(res.data.length)).catch(() => {});
        hämtaKundförslag().then(res => setFortnoxAntal(res.data.length)).catch(() => {});
        hämtaLagervarningar().then(res => setLagerAntal(res.data.antal_kritiska)).catch(() => {});
        hämtaDubbletter().then(res => setDubblettAntal(res.data.length)).catch(() => {});
        hämtaIntagsfel().then(res => setIntagsfelAntal(res.data.length)).catch(() => {});
        if (harInkop) hämtaAntalAttÅtgärda().then(res => setInkopAntal(res.data.antal)).catch(() => {});
      }, 300);
    };
    window.addEventListener('vinsales:att-atgarda-andrad', uppdatera);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('vinsales:att-atgarda-andrad', uppdatera);
    };
  }, [harInkop]);

  const påÅtgärdaSida = ÅTGÄRDA_SIDOR.some(s => location.pathname.startsWith(s));
  const [åtgärdaÖppen, setÅtgärdaÖppen] = useState(påÅtgärdaSida);
  // Vid varje sidbyte: öppen på gruppens sidor, ihopfälld överallt annars.
  // Pilen styr fortfarande gruppen så länge man står kvar på samma sida.
  useEffect(() => {
    setÅtgärdaÖppen(påÅtgärdaSida);
  }, [location.pathname, påÅtgärdaSida]);

  const aktivModul = useAktivModul();
  const åtgärdaTotalt = attKopplaAntal + intagsfelAntal + fortnoxAntal + lagerAntal + matchAntal + dubblettAntal;

  // Fäll ut gruppen automatiskt när man navigerar in i den.
  useEffect(() => {
    if (påÅtgärdaSida) setÅtgärdaÖppen(true);
  }, [påÅtgärdaSida]);

  // Räknar okopplade mejlaktiviteter. Hämtas om vid varje sidbyte, så att
  // siffran stämmer direkt efter att något kopplats.
  useEffect(() => {
    let avbruten = false;
    hämtaMoten()
      .then(res => {
        if (avbruten) return;
        const mina = res.data.filter(m =>
          (!m.kund_id || (m as any).okand_mottagare) &&
          (profil?.roll === 'admin' || m.säljare === profil?.namn)
        );
        setAttKopplaAntal(mina.length);
      })
      .catch(() => { /* siffran är en hjälp, inte kritisk */ });
    hämtaKundförslag()
      .then(res => { if (!avbruten) setFortnoxAntal(res.data.length); })
      .catch(() => { /* siffran är en hjälp, inte kritisk */ });
    // Bara kritiska räknas — "låg" och "bevaka" är inte bråttom.
    hämtaLagervarningar()
      .then(res => { if (!avbruten) setLagerAntal(res.data.antal_kritiska); })
      .catch(() => { /* siffran är en hjälp, inte kritisk */ });
    hämtaDubbletter()
      .then(res => { if (!avbruten) setDubblettAntal(res.data.length); })
      .catch(() => {});
    hämtaOklaraMatchningar()
      .then(res => { if (!avbruten) setMatchAntal(res.data.length); })
      .catch(() => { /* siffran är en hjälp, inte kritisk */ });
    hämtaIntagsfel()
      .then(res => { if (!avbruten) setIntagsfelAntal(res.data.length); })
      .catch(() => { /* siffran är en hjälp, inte kritisk */ });
    if (harInkop) {
      hämtaAntalAttÅtgärda()
        .then(res => { if (!avbruten) setInkopAntal(res.data.antal); })
        .catch(() => { /* siffran är en hjälp, inte kritisk */ });
    }
    return () => { avbruten = true; };
  }, [location.pathname, profil?.namn, profil?.roll, harInkop]);

  function hanteraSökning(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && sökning.trim()) {
      navigate(`/alla-kunder?q=${encodeURIComponent(sökning.trim())}`);
      setSökning('');
      onNavigate?.();
    }
  }

  // Regel: guldfyllning är för handlingar (knappar). Navigering markeras med
  // ton: den aktiva sidan får en svag guldton och ett guldstreck till vänster,
  // så att modulväljaren ovanför förblir det tyngsta i menyn.
  const länkStyle = ({ isActive }: { isActive: boolean }): React.CSSProperties => ({
    display: 'block',
    padding: '10px 14px 10px 12px',
    borderRadius: '0 4px 4px 0',
    borderLeft: `2px solid ${isActive ? TEMA.accent : 'transparent'}`,
    fontSize: '14px',
    fontFamily: "'Inter', sans-serif",
    color: isActive ? TEMA.ink : TEMA.inkSoft,
    background: isActive ? 'rgba(224,168,62,0.12)' : 'transparent',
    fontWeight: isActive ? 500 : 400,
    textDecoration: 'none',
    marginBottom: '4px',
  });

  // Undersidorna dras in och är något mindre, så att gruppen läses som en enhet.
  const underLänkStyle = ({ isActive }: { isActive: boolean }): React.CSSProperties => ({
    ...länkStyle({ isActive }),
    padding: '8px 14px 8px 24px',
    fontSize: '13.5px',
  });

  function pillStyle(aktiv: boolean): React.CSSProperties {
    return {
      fontFamily: "'IBM Plex Mono', monospace",
      fontSize: '11px',
      fontWeight: 500,
      lineHeight: 1,
      minWidth: '18px',
      textAlign: 'center',
      padding: '4px 8px',
      borderRadius: '20px',
      flexShrink: 0,
      background: aktiv ? 'rgba(224,168,62,0.2)' : 'rgba(224,168,62,0.12)',
      border: '1px solid rgba(224,168,62,0.35)',
      color: TEMA.accent,
    };
  }

  const restaurangLänkar = (
    <>
      <NavLink to="/idag" end style={länkStyle} onClick={onNavigate}>Startsida</NavLink>
      <NavLink to="/att-gora" style={länkStyle} onClick={onNavigate}>Att göra</NavLink>
      <NavLink to="/registrera" style={länkStyle} onClick={onNavigate}>Registrera aktivitet</NavLink>
      <NavLink to="/alla-kunder" style={länkStyle} onClick={onNavigate}>Alla kunder</NavLink>
      <NavLink to="/statistik" style={länkStyle} onClick={onNavigate}>Statistik</NavLink>
      {/* Att åtgärda — listor som väntar på handling. Summan visas på rubriken
          så att den syns även när gruppen är ihopfälld. */}
      <button
        onClick={() => setÅtgärdaÖppen(o => !o)}
        aria-expanded={åtgärdaÖppen}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
          width: '100%', padding: '10px 14px', marginBottom: '4px',
          borderRadius: '4px', border: 'none', background: 'transparent',
          color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif", fontSize: '14px',
          cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          Att åtgärda
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" style={{
            transform: åtgärdaÖppen ? 'rotate(90deg)' : 'none',
            transition: 'transform 0.15s ease', opacity: 0.7,
          }}>
            <path d="M2 1L6 4L2 7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        {!åtgärdaÖppen && åtgärdaTotalt > 0 && (
          <span style={pillStyle(false)}>{åtgärdaTotalt}</span>
        )}
      </button>

      {åtgärdaÖppen && (
        <>
          <NavLink to="/att-koppla" style={underLänkStyle} onClick={onNavigate}>
            {({ isActive }) => (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                Aktiviteter att koppla
                {attKopplaAntal > 0 && <span style={pillStyle(isActive)}>{attKopplaAntal}</span>}
              </span>
            )}
          </NavLink>
          {/* Visas bara när något väntar: i vanliga fall läses alla mejl in. */}
          {intagsfelAntal > 0 && (
            <NavLink to="/inlasningsfel" style={underLänkStyle} onClick={onNavigate}>
              {({ isActive }) => (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                  Mejl som inte lästes in
                  <span style={pillStyle(isActive)}>{intagsfelAntal}</span>
                </span>
              )}
            </NavLink>
          )}
          <NavLink to="/nya-fortnox-kunder" style={underLänkStyle} onClick={onNavigate}>
            {({ isActive }) => (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                Nya kunder att stämma av
                {fortnoxAntal > 0 && <span style={pillStyle(isActive)}>{fortnoxAntal}</span>}
              </span>
            )}
          </NavLink>
          <NavLink to="/artikelmatchning" style={underLänkStyle} onClick={onNavigate}>
            {({ isActive }) => (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                Viner att stämma av
                {matchAntal > 0 && <span style={pillStyle(isActive)}>{matchAntal}</span>}
              </span>
            )}
          </NavLink>
          <NavLink to="/dubbletter" style={underLänkStyle} onClick={onNavigate}>
            {({ isActive }) => (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                Möjliga dubbletter
                {dubblettAntal > 0 && <span style={pillStyle(isActive)}>{dubblettAntal}</span>}
              </span>
            )}
          </NavLink>
          <NavLink to="/lager" style={underLänkStyle} onClick={onNavigate}>
            {({ isActive }) => (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                Lågt lagersaldo
                {lagerAntal > 0 && <span style={pillStyle(isActive)}>{lagerAntal}</span>}
              </span>
            )}
          </NavLink>
        </>
      )}
    </>
  );

  // Inköp. Inköpsplanen och Årsplanen är en sida sedan 29 okt 2026.
  // `end` på Startsida: annars markeras den som aktiv på alla /inkop/...-sidor.
  const inkopLänkar = (
    <>
      <NavLink to="/inkop" end style={länkStyle} onClick={onNavigate}>Startsida</NavLink>
      <NavLink to="/inkop/plan" style={länkStyle} onClick={onNavigate}>Inköpsplan</NavLink>
      <NavLink to="/inkop/lista" style={länkStyle} onClick={onNavigate}>Pågående inköp</NavLink>
      <NavLink to="/inkop/producenter" style={länkStyle} onClick={onNavigate}>Producenter</NavLink>
      {/* Prissättningen är hemlig och visas inte i demon. */}
      {!demo && <NavLink to="/inkop/prissattning" style={länkStyle} onClick={onNavigate}>Prissättning</NavLink>}
      <NavLink to="/inkop/att-atgarda" style={länkStyle} onClick={onNavigate}>
        {({ isActive }) => (
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
            Att åtgärda
            {inkopAntal > 0 && <span style={pillStyle(isActive)}>{inkopAntal}</span>}
          </span>
        )}
      </NavLink>
    </>
  );

  // `end` på Startsida: annars matchar /anbud som prefix och markeras
  // som aktiv även på alla /anbud/...-undersidor.
  const anbudLänkar = (
    <>
      <NavLink to="/anbud" end style={länkStyle} onClick={onNavigate}>Startsida</NavLink>
      <NavLink to="/anbudskalender" style={länkStyle} onClick={onNavigate}>Kalender</NavLink>
      {!demo && <NavLink to="/anbud/affarsmojligheter" style={länkStyle} onClick={onNavigate}>Offertförfrågningar</NavLink>}
      {!demo && <NavLink to="/anbud/offertpipeline" style={länkStyle} onClick={onNavigate}>Offerter</NavLink>}
      <NavLink to="/anbud/aktiviteter" style={länkStyle} onClick={onNavigate}>Aktiviteter</NavLink>
      {!demo && <NavLink to="/anbud/produktlivscykel" style={länkStyle} onClick={onNavigate}>Produktlivscykel</NavLink>}
      {!demo && <NavLink to="/anbud/orderhistorik" style={länkStyle} onClick={onNavigate}>Orderhistorik</NavLink>}
    </>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '28px 20px', boxSizing: 'border-box' }}>
      {/* I mobilmenyn (onNavigate) står logotypen redan i sidhuvudet ovanför. */}
      {!onNavigate && (
        <Link
          to="/"
          aria-label="Till startsidan"
          style={{ fontFamily: "'Fraunces', serif", fontSize: '20px', marginBottom: '32px', color: TEMA.ink, textDecoration: 'none', alignSelf: 'flex-start' }}
        >
          Vin<em style={{ fontStyle: 'italic', color: TEMA.accent }}>sales</em>
        </Link>
      )}

      {/* Modulväljaren byter modul och landar på modulens startsida. I mobilmenyn
          (onNavigate) står den i stället i sidhuvudet. */}
      {!onNavigate && <Modulvaljare antal={{ restaurang: åtgärdaTotalt, inkop: inkopAntal }} />}

      {aktivModul === 'anbud' ? anbudLänkar : aktivModul === 'inkop' ? inkopLänkar : restaurangLänkar}


      <div style={{ marginTop: 'auto' }}>
        <input
          type="text"
          placeholder="Sök kund..."
          value={sökning}
          onChange={e => setSökning(e.target.value)}
          onKeyDown={hanteraSökning}
          style={{
            width: '100%',
            padding: '9px 12px',
            borderRadius: '2px',
            fontSize: '13px',
            fontFamily: "'Inter', sans-serif",
            border: `1px solid ${TEMA.border}`,
            background: TEMA.bgPanel,
            color: TEMA.ink,
            outline: 'none',
            boxSizing: 'border-box',
            marginBottom: '14px',
          }}
        />
        {profil && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '10px' }}>
            <NavLink to="/profil" onClick={onNavigate} style={{ textDecoration: 'none', minWidth: 0, fontFamily: "'Inter', sans-serif" }}>
              <span style={{ display: 'block', fontSize: '13.5px', color: TEMA.ink, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {profil.namn}
              </span>
              <span style={{ display: 'block', fontSize: '12px', color: TEMA.inkFaint, marginTop: '1px' }}>
                {profil.roll === 'admin' ? 'Admin' : profil.roll === 'säljare' ? 'Säljare' : profil.roll}
              </span>
            </NavLink>
            <NavLink
              to="/profil"
              onClick={onNavigate}
              title="Inställningar"
              aria-label="Inställningar"
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '32px', height: '32px', borderRadius: '4px', flexShrink: 0,
                color: isActive || ['/admin', '/handelselogg'].some(p => window.location.pathname.startsWith(p)) ? TEMA.accent : TEMA.inkFaint,
                border: `1px solid ${isActive || ['/admin', '/handelselogg'].some(p => window.location.pathname.startsWith(p)) ? TEMA.accent : TEMA.border}`,
              })}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </NavLink>
          </div>
        )}
        <button
          onClick={loggaUt}
          style={{
            width: '100%',
            padding: '9px',
            borderRadius: '2px',
            border: `1px solid ${TEMA.border}`,
            background: 'transparent',
            color: TEMA.inkSoft,
            fontFamily: "'Inter', sans-serif",
            fontSize: '13px',
            cursor: 'pointer',
          }}
        >
          Logga ut
        </button>
      </div>
    </div>
  );
}